import { generateGUID } from '@awork/_shared/functions/guid-generator'
import { AccessLevels } from '@awork/features/workspace/models/permissions.model'
import { FilterContributor } from './filter-contributor.model'
import { FilterTeamAccess } from './filter-team-access.model'
import type { FilterEntityType } from './filters.types'
import { FilterOperator } from './filters.types'
import { FILTER_CONFIGURATION } from './filter-property-configuration.model'
import { findConfigurationForField, getDefaultOperator } from './filter-property-utils'

interface IFilterEntity {
  id: string
  name: string
  entityType: FilterEntityType
  /**
   * Legacy flag that marks a filter as shared.
   * @deprecated TODO: Remove when backend migrates to workspaceAccessLevel
   */
  isShared?: boolean
  workspaceAccessLevel?: string
  contributors?: FilterContributor[]
  teams?: FilterTeamAccess[]
  filterQuery: string
  filterDefinition: FilterDefinition
  userId?: string
  createdOn?: Date
  createdBy?: string
  updatedOn?: Date
  updatedBy?: string
}

export class FilterEntity implements IFilterEntity {
  id: string
  name: string
  entityType: FilterEntityType
  /**
   * Legacy flag that marks a filter as shared.
   * @deprecated TODO: Remove when backend migrates to workspaceAccessLevel
   */
  isShared?: boolean
  workspaceAccessLevel?: string
  contributors?: FilterContributor[]
  teams?: FilterTeamAccess[]
  filterQuery: string
  filterDefinition: FilterDefinition
  userId?: string
  createdOn?: Date
  createdBy?: string
  updatedOn?: Date
  updatedBy?: string

  constructor(filter: IFilterEntity) {
    Object.assign(this, filter)

    if (this.filterDefinition) {
      this.filterDefinition = new FilterDefinition(this.filterDefinition)
    }

    if (this.contributors) {
      this.contributors = this.contributors.map(c => new FilterContributor(c))
    }

    if (this.teams) {
      this.teams = this.teams.map(t => new FilterTeamAccess(t))
    }
  }

  /**
   * Checks if the given user has manage access to this filter.
   * Uses precedence-based evaluation consistent with document sharing:
   * contributor access > team access > workspace access.
   * The most specific permission source wins — a more specific read overrides a broader manage.
   * @param {string} userId The user ID to check
   * @param {string[]} userTeamIds The team IDs the user belongs to
   * @returns {boolean} true if the user can manage this filter
   */
  canManage(userId: string, userTeamIds: string[]): boolean {
    if (this.userId === userId) {
      return true
    }

    const contributor = this.contributors?.find(c => c.userId === userId)

    if (contributor) {
      return contributor.accessLevel === AccessLevels.Manage
    }

    const matchingTeams = this.teams?.filter(t => userTeamIds.includes(t.teamId))

    if (matchingTeams?.length) {
      return matchingTeams.some(t => t.accessLevel === AccessLevels.Manage)
    }

    return this.workspaceAccessLevel === AccessLevels.Manage
  }

  /**
   * Validates the filter entity
   * @returns {boolean} true if the filter entity is valid
   */
  isValid(): boolean {
    if (!this.entityType) {
      return false
    }

    if (!this.filterDefinition) {
      return false
    }

    return this.filterDefinition.isValid()
  }

  /**
   * Returns whether the filter has no filter conditions
   * @returns {boolean}
   */
  isEmpty(): boolean {
    return !this.filterDefinition?.conditionGroups?.some(g => (g.conditions?.length ?? 0) > 0)
  }

  /**
   * Returns whether the filter can be displayed as a compact filter
   * @returns {boolean}
   */
  isCompact(): boolean {
    return this.filterDefinition.isCompact()
  }
}

interface IDraftFilterEntity {
  id?: string | null
  userId?: string
  entityType: FilterEntityType
  filterDefinition: FilterDefinition
  filterId?: string
  createdOn?: Date
  createdBy?: string
  updatedOn?: Date
  updatedBy?: string
}

export class DraftFilterEntity implements IDraftFilterEntity {
  id: string
  userId?: string
  entityType: FilterEntityType
  filterDefinition: FilterDefinition
  filterId?: string
  createdOn?: Date
  createdBy?: string
  updatedOn?: Date
  updatedBy?: string

  constructor(draft: IDraftFilterEntity) {
    Object.assign(this, { id: generateGUID(), ...draft })

    if (this.filterDefinition) {
      this.filterDefinition = new FilterDefinition(this.filterDefinition)
    }
  }

  /**
   * Validates the draft filter entity
   * @returns {boolean} true if the draft filter entity is valid
   */
  isValid(): boolean {
    if (!this.entityType) {
      return false
    }

    if (!this.filterDefinition) {
      return false
    }

    return this.filterDefinition.isValid()
  }

  /**
   * Returns whether the draft has no filter conditions
   * @returns {boolean}
   */
  isEmpty(): boolean {
    return !this.filterDefinition?.conditionGroups?.some(g => (g.conditions?.length ?? 0) > 0)
  }

  /**
   * Returns whether the filter can be displayed as a compact filter
   * @returns {boolean}
   */
  isCompact(): boolean {
    return this.filterDefinition.isCompact()
  }
}

export class FilterDefinition {
  entityType: FilterEntityType
  rootConnector: FilterConnector
  conditionGroups: FilterConditionGroup[]

  constructor(filterDefinition: {
    entityType: FilterEntityType
    rootConnector: FilterConnector
    conditionGroups: FilterConditionGroup[]
  }) {
    Object.assign(this, filterDefinition)

    if (this.conditionGroups) {
      this.conditionGroups = this.conditionGroups.map(group => new FilterConditionGroup(group))
    }
  }

  /**
   * Validates the filter definition
   * @returns {boolean} true if the filter definition is valid
   */
  isValid(): boolean {
    if (!this.entityType || !this.rootConnector) {
      return false
    }

    if (!this.conditionGroups || this.conditionGroups.length === 0) {
      return false
    }

    return this.conditionGroups.every(group => group.isValid())
  }

  /**
   * Returns whether the filter can be displayed as a compact filter.
   * A filter is compact when it has at most one condition group and all conditions
   * use compact properties with their default operators.
   * @returns {boolean}
   */
  isCompact(): boolean {
    if (this.rootConnector !== FilterConnector.And) {
      return false
    }

    if (this.conditionGroups.length > 1) {
      return false
    }

    if (this.conditionGroups.length === 0) {
      return true
    }

    return this.conditionGroups[0].isCompact(this.entityType)
  }
}

export class FilterConditionGroup {
  connector: FilterConnector
  conditions: FilterCondition[]

  constructor(filterConditionGroup: { connector: FilterConnector; conditions: FilterCondition[] }) {
    Object.assign(this, filterConditionGroup)

    if (this.conditions) {
      this.conditions = this.conditions.map(condition => new FilterCondition(condition))
    }
  }

  /**
   * Returns whether all conditions in this group use compact properties with their default operators.
   * @param {FilterEntityType} entityType The entity type to resolve property configurations
   * @returns {boolean}
   */
  isCompact(entityType: FilterEntityType): boolean {
    if (this.connector !== FilterConnector.And) {
      return false
    }

    const conditions = this.conditions ?? []

    return conditions.every(condition => condition.isCompact(entityType))
  }

  /**
   * Validates the filter condition group
   * @returns {boolean} true if the filter condition group is valid
   */
  isValid(): boolean {
    if (!this.connector) {
      return false
    }

    if (!this.conditions || this.conditions.length === 0) {
      return false
    }

    return this.conditions.every(condition => condition.isValid())
  }
}

export enum FilterConnector {
  And = 'And',
  Or = 'Or'
}

interface IFilterCondition {
  field: string
  type: FilterConditionType
  operator: FilterOperator
  values?: FilterValue[]
  customFieldInformation?: FilterCustomFieldInformation
}

export class FilterCondition implements IFilterCondition {
  field: string
  type: FilterConditionType
  operator: FilterOperator
  values?: FilterValue[]
  customFieldInformation?: FilterCustomFieldInformation

  constructor(filterCondition: IFilterCondition) {
    const { field, type, operator, values, customFieldInformation } = filterCondition
    Object.assign(this, { field, type, operator, values, customFieldInformation })
  }

  /**
   * Validates the filter condition
   * @returns {boolean} true if the filter condition is valid
   */
  isValid(): boolean {
    return this.getValidationMessage() === null
  }

  /**
   * Returns whether this condition is compact, meaning it uses a compact property with its default operator.
   * @param {FilterEntityType} entityType The entity type to resolve the property configuration
   * @returns {boolean} true if the condition uses a compact property with its default operator
   */
  isCompact(entityType: FilterEntityType): boolean {
    if (this.customFieldInformation) {
      return false
    }

    if (!this.field) {
      return true
    }

    const configurations = FILTER_CONFIGURATION[entityType] ?? []
    const config = findConfigurationForField(this.field, configurations)

    if (!config?.isCompact) {
      return false
    }

    return this.operator === getDefaultOperator(config)
  }

  /**
   * Gets a validation message for the filter condition.
   * @returns {string | null} The validation message if invalid, otherwise null
   */
  getValidationMessage(): string | null {
    if (!this.field) {
      return q.translations.Filter.validation.noProperty
    }

    if (!this.operator || !this.type) {
      return q.translations.Filter.validation.noOperator
    }

    const operatorsWithoutValues = [
      FilterOperator.IsSet,
      FilterOperator.IsNotSet,
      FilterOperator.WithinThisMonth,
      FilterOperator.WithinThisWeek,
      FilterOperator.WithinLastWeek,
      FilterOperator.WithinLastMonth
    ]

    if (!operatorsWithoutValues.includes(this.operator)) {
      if (!this.values || this.values.length === 0) {
        return q.translations.Filter.validation.noValue
      }

      const hasEmptyConstantStringValue = this.values.some(
        value =>
          value.filterValueKind === FilterValueKind.Constant &&
          (value.constantValue == null || (typeof value.constantValue === 'string' && !value.constantValue.trim()))
      )

      if (hasEmptyConstantStringValue) {
        return q.translations.Filter.validation.noValue
      }
    }

    return null
  }
}

export enum FilterConditionType {
  Comparison = 'Comparison',
  Collection = 'Collection',
  CustomField = 'CustomField'
}

export type FilterValue = FilterValueConstant | FilterValueComputed

export interface FilterValueConstant {
  filterValueKind: FilterValueKind.Constant
  constantValue: string | number | boolean
}

export interface FilterValueComputed {
  filterValueKind: FilterValueKind.Computed
  computedValueKind: FilterComputedValueKind
  daysOffset?: number
}

export interface FilterCustomFieldInformation {
  customFieldDefinitionId: string
  customFieldValueType: FilterCustomFieldValueType
}

export enum FilterCustomFieldValueType {
  UserId = 'UserId',
  Number = 'Number',
  SelectionOptionId = 'SelectionOptionId',
  Text = 'Text',
  Date = 'Date',
  Boolean = 'Boolean',
  Client = 'Client'
}

export enum FilterValueKind {
  Constant = 'Constant',
  Computed = 'Computed'
}

export enum FilterComputedValueKind {
  Me = 'Me',
  Now = 'Now',
  Today = 'Today',
  StartOfWeek = 'StartOfWeek',
  EndOfWeek = 'EndOfWeek',
  StartOfMonth = 'StartOfMonth',
  EndOfMonth = 'EndOfMonth'
}

/**
 * Creates an empty filter condition
 * @returns {FilterCondition} A new empty filter condition instance
 */
export const createEmptyFilterCondition = (): FilterCondition =>
  new FilterCondition({
    field: null,
    type: null,
    operator: null
  })

/**
 * Creates an empty filter condition group
 * @returns {FilterConditionGroup} A new empty filter condition group instance
 */
export const createEmptyFilterConditionGroup = (): FilterConditionGroup =>
  new FilterConditionGroup({
    connector: FilterConnector.And,
    conditions: [createEmptyFilterCondition()]
  })

/**
 * Creates an empty filter definition
 * @param {FilterEntityType} entityType The entity type for the filter
 * @param {boolean} [includeEmptyCondition=true] Whether to include an empty condition in the condition group
 * @returns {FilterDefinition} A new empty filter definition instance
 */
export const createEmptyFilterDefinition = (
  entityType: FilterEntityType,
  includeEmptyCondition = true
): FilterDefinition =>
  new FilterDefinition({
    entityType,
    rootConnector: FilterConnector.And,
    conditionGroups: includeEmptyCondition
      ? [createEmptyFilterConditionGroup()]
      : [new FilterConditionGroup({ connector: FilterConnector.And, conditions: [] })]
  })

/**
 * Creates an empty draft filter entity
 * @param {FilterEntityType} entityType The entity type for the draft filter
 * @param {boolean} [includeEmptyCondition=true] Whether to include an empty condition in the condition group
 * @returns {DraftFilterEntity} A new empty draft filter entity instance
 */
export const createEmptyDraftFilterEntity = (
  entityType: FilterEntityType,
  includeEmptyCondition = true
): DraftFilterEntity =>
  new DraftFilterEntity({
    id: null,
    entityType,
    filterDefinition: createEmptyFilterDefinition(entityType, includeEmptyCondition)
  })
